"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  RESULT_CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  DensityChart,
  Probability,
} from "@/components/tools/stats-distributions/charts";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import { densityGeometry } from "@/lib/tool-kit/stats-distributions/chart";
import {
  apaPText,
  fixed,
  formatDf,
  type NumberFormatter,
  probabilityText,
} from "@/lib/tool-kit/stats-distributions/format";
import {
  apaReport,
  checkPValueInput,
  computePValue,
  type PStatistic,
  type PValueInput,
  type PValueProblem,
  type PValueResult,
  shadedRegions,
  sketchPdf,
  sketchRange,
  type Tail,
} from "@/lib/tool-kit/stats-distributions/p-value";

/** English strings. A translated content file overrides these through `ui`. */
export const P_VALUE_CALCULATOR_STRINGS = {
  statisticLabel: "Test statistic",
  statisticZ: "z (normal)",
  statisticT: "t (Student)",
  statisticChi2: "χ² (chi-square)",
  statisticF: "F (ANOVA, regression)",
  statisticR: "r (Pearson correlation)",
  valueLabel: "Value of {symbol}",
  dfLabel: "Degrees of freedom (df)",
  df1Label: "Numerator df (df₁)",
  df2Label: "Denominator df (df₂)",
  nLabel: "Sample size (n pairs)",
  tailLabel: "Alternative hypothesis",
  tailTwo: "Two-tailed (≠)",
  tailLeft: "Left-tailed (<)",
  tailRight: "Right-tailed (>)",
  alphaLabel: "Significance level (α)",
  results: "Results",
  pValue: "p-value",
  statistic: "Test statistic",
  decisionLabel: "Decision at α = {alpha}",
  significant: "Significant",
  notSignificant: "Not significant",
  significantSentence:
    "{p} is at or below α = {alpha}, so the result is statistically significant at the {alpha} level: reject the null hypothesis.",
  notSignificantSentence:
    "{p} is above α = {alpha}, so the result is not statistically significant at the {alpha} level: do not reject the null hypothesis. That is not evidence the null hypothesis is true — only that this test could not rule it out.",
  roundingNote:
    "Rounded to three decimals, p reads the same as α. Report more decimals (p = {exact}) so the reader can see which side of {alpha} it falls on.",
  apaHeading: "APA 7 report",
  apaNote:
    "Italicise the statistic and p. For χ² tests, APA also asks for the sample size: χ²(df, N = …).",
  copyApa: "Copy",
  copied: "Copied!",
  allTails: "The same statistic under each alternative",
  alternativeColumn: "Alternative",
  pColumn: "p-value",
  conversion:
    "r is tested as t on n − 2 = {df} df: t = r √((n − 2) / (1 − r²)) = {t}",
  sketchHeading: "Where the p-value comes from",
  sketchLabel: "{distribution} with the area equal to the p-value shaded",
  sketchCaption:
    "The shaded area is the p-value ({p}); the dashed line marks your statistic.",
  offScale:
    "Your statistic lies beyond the edge of the sketch, so the shaded tail is too thin to see.",
  distributionZ: "Standard normal distribution",
  distributionT: "t distribution with {df} df",
  distributionChi2: "Chi-square distribution with {df} df",
  distributionF: "F distribution with {df1} and {df2} df",
  rightTailNote:
    "χ² tests of fit or independence and F tests in ANOVA or regression are right-tailed. Use left- or two-tailed only for a test about a variance.",
  enterPrompt: "Enter the value of your test statistic to see its p-value.",
  problemDf: "Enter degrees of freedom greater than 0.",
  problemDf1: "Enter a numerator df greater than 0.",
  problemDf2: "Enter a denominator df greater than 0.",
  problemN: "Enter the sample size as a whole number of at least 3.",
  problemNegative: "χ² and F statistics cannot be negative.",
  problemR: "A correlation must lie strictly between −1 and 1.",
  problemAlpha: "Enter α between 0 and 1, such as 0.05.",
} as const;

type Strings = typeof P_VALUE_CALCULATOR_STRINGS;

const SYMBOLS: Record<PStatistic, string> = {
  z: "z",
  t: "t",
  chi2: "χ²",
  f: "F",
  r: "r",
};

const ONE_SIDED: ReadonlySet<PStatistic> = new Set(["chi2", "f"]);

const PROBLEM_KEYS: Record<PValueProblem, keyof Strings> = {
  df: "problemDf",
  df1: "problemDf1",
  df2: "problemDf2",
  n: "problemN",
  negative: "problemNegative",
  rRange: "problemR",
};

function alphaText(alpha: number, fmt: NumberFormatter): string {
  return fixed(alpha, Math.max(2, decimalsOf(alpha)), fmt, false);
}

/** The fewest decimals (from 4) at which p no longer rounds to α. */
function separatingDecimals(p: number, alpha: number): number {
  for (let decimals = 4; decimals < 8; decimals++) {
    const scale = 10 ** decimals;
    if (Math.abs(Math.round(p * scale) / scale - alpha) > 1e-12) {
      return decimals;
    }
  }
  return 8;
}

function decimalsOf(value: number): number {
  const text = String(value);
  const dot = text.indexOf(".");
  return dot === -1 ? 0 : Math.min(4, text.length - dot - 1);
}

function distributionName(
  result: PValueResult,
  strings: Strings,
  fmt: NumberFormatter
) {
  if (result.distribution === "z") {
    return strings.distributionZ;
  }
  if (result.distribution === "t") {
    return t(strings.distributionT, { df: formatDf(result.df as number, fmt) });
  }
  if (result.distribution === "chi2") {
    return t(strings.distributionChi2, {
      df: formatDf(result.df as number, fmt),
    });
  }
  return t(strings.distributionF, {
    df1: formatDf(result.df1 as number, fmt),
    df2: formatDf(result.df2 as number, fmt),
  });
}

function Sketch({
  result,
  tail,
  strings,
  fmt,
}: {
  result: PValueResult;
  tail: Tail;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const geometry = useMemo(() => {
    const [xMin, xMax] = sketchRange(result);
    const x = result.testValue;
    const symmetric =
      result.distribution === "z" || result.distribution === "t";
    const markers =
      tail === "two" && symmetric ? [-Math.abs(x), Math.abs(x)] : [x];
    return densityGeometry({
      pdf: sketchPdf(result),
      xMin,
      xMax,
      shaded: shadedRegions(result, tail),
      markers,
    });
  }, [result, tail]);
  const name = distributionName(result, strings, fmt);
  return (
    <div className={`${CARD} space-y-3`}>
      <h3 className={WIDGET_H3}>{strings.sketchHeading}</h3>
      <DensityChart
        format={(value) => fmt(value, { maximumFractionDigits: 2 })}
        geometry={geometry}
        label={t(strings.sketchLabel, { distribution: name })}
      />
      <p className="text-muted-foreground text-sm">
        {name}.{" "}
        {t(strings.sketchCaption, {
          p: probabilityText(result.p, fmt),
        })}
      </p>
      {geometry.markers.some((marker) => marker.offScale) ? (
        <p className="text-muted-foreground text-sm">{strings.offScale}</p>
      ) : null}
    </div>
  );
}

function Decision({
  p,
  alpha,
  strings,
  fmt,
}: {
  p: number;
  alpha: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const alphaShown = alphaText(alpha, fmt);
  const pShown = apaPText(p, fmt);
  const significant = p <= alpha;
  const roundsToAlpha =
    p !== alpha && Math.abs(Math.round(p * 1000) / 1000 - alpha) < 1e-12;
  return (
    <div className={`${RESULT_CARD} space-y-2 text-foreground text-sm`}>
      <p>
        {t(
          significant
            ? strings.significantSentence
            : strings.notSignificantSentence,
          {
            p: pShown,
            alpha: alphaShown,
          }
        )}
      </p>
      {roundsToAlpha ? (
        <p className="text-muted-foreground">
          {t(strings.roundingNote, {
            exact: fixed(p, separatingDecimals(p, alpha), fmt, false),
            alpha: alphaShown,
          })}
        </p>
      ) : null}
    </div>
  );
}

function TailTable({
  result,
  strings,
  fmt,
}: {
  result: PValueResult;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const rows: [string, number][] = [
    [strings.tailTwo, Math.min(1, 2 * Math.min(result.lower, result.upper))],
    [strings.tailLeft, result.lower],
    [strings.tailRight, result.upper],
  ];
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <caption className="px-3 pt-3 text-left text-muted-foreground text-sm">
          {strings.allTails}
        </caption>
        <thead>
          <tr>
            <th className={TH} scope="col">
              {strings.alternativeColumn}
            </th>
            <th className={`${TH} text-right`} scope="col">
              {strings.pColumn}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label}>
              <td className={TD}>{label}</td>
              <td className={`${TD} text-right font-mono`}>
                <Probability fmt={fmt} value={value} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface FormState {
  statistic: PStatistic;
  valueText: string;
  dfText: string;
  df1Text: string;
  df2Text: string;
  nText: string;
  tail: Tail;
  alphaInput: string;
}

const INITIAL: FormState = {
  statistic: "t",
  valueText: "2.31",
  dfText: "24",
  df1Text: "2",
  df2Text: "27",
  nText: "30",
  tail: "two",
  alphaInput: "0.05",
};

function DfFields({
  form,
  update,
  strings,
}: {
  form: FormState;
  update: (patch: Partial<FormState>) => void;
  strings: Strings;
}) {
  if (form.statistic === "t" || form.statistic === "chi2") {
    return (
      <TextField
        id="pv-df"
        label={strings.dfLabel}
        numeric
        onChange={(dfText) => update({ dfText })}
        value={form.dfText}
      />
    );
  }
  if (form.statistic === "f") {
    return (
      <>
        <TextField
          id="pv-df1"
          label={strings.df1Label}
          numeric
          onChange={(df1Text) => update({ df1Text })}
          value={form.df1Text}
        />
        <TextField
          id="pv-df2"
          label={strings.df2Label}
          numeric
          onChange={(df2Text) => update({ df2Text })}
          value={form.df2Text}
        />
      </>
    );
  }
  if (form.statistic === "r") {
    return (
      <TextField
        id="pv-n"
        label={strings.nLabel}
        numeric
        onChange={(nText) => update({ nText })}
        value={form.nText}
      />
    );
  }
  return null;
}

function PValueForm({
  form,
  update,
  strings,
}: {
  form: FormState;
  update: (patch: Partial<FormState>) => void;
  strings: Strings;
}) {
  const statisticOptions = [
    { value: "z" as const, label: strings.statisticZ },
    { value: "t" as const, label: strings.statisticT },
    { value: "chi2" as const, label: strings.statisticChi2 },
    { value: "f" as const, label: strings.statisticF },
    { value: "r" as const, label: strings.statisticR },
  ];
  const tailOptions = [
    { value: "two" as const, label: strings.tailTwo },
    { value: "left" as const, label: strings.tailLeft },
    { value: "right" as const, label: strings.tailRight },
  ];
  return (
    <section className="space-y-5">
      <PillGroup
        label={strings.statisticLabel}
        name="pv-statistic"
        onChange={(statistic) =>
          update({
            statistic,
            tail: ONE_SIDED.has(statistic) ? "right" : "two",
          })
        }
        options={statisticOptions}
        value={form.statistic}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          id="pv-value"
          label={t(strings.valueLabel, { symbol: SYMBOLS[form.statistic] })}
          numeric
          onChange={(valueText) => update({ valueText })}
          value={form.valueText}
        />
        <DfFields form={form} strings={strings} update={update} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          hint={
            ONE_SIDED.has(form.statistic) ? strings.rightTailNote : undefined
          }
          id="pv-tail"
          label={strings.tailLabel}
          onChange={(tail) => update({ tail })}
          options={tailOptions}
          value={form.tail}
        />
        <TextField
          id="pv-alpha"
          label={strings.alphaLabel}
          numeric
          onChange={(alphaInput) => update({ alphaInput })}
          value={form.alphaInput}
        />
      </div>
    </section>
  );
}

function readInput(form: FormState): PValueInput | null {
  const value = parseNumberInput(form.valueText);
  if (value === null) {
    return null;
  }
  return {
    statistic: form.statistic,
    value,
    tail: form.tail,
    df: parseNumberInput(form.dfText) ?? undefined,
    df1: parseNumberInput(form.df1Text) ?? undefined,
    df2: parseNumberInput(form.df2Text) ?? undefined,
    n: parseIntegerInput(form.nText) ?? undefined,
  };
}

function PValueResults({
  input,
  result,
  alpha,
  strings,
  fmt,
}: {
  input: PValueInput;
  result: PValueResult;
  alpha: number | null;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const report = apaReport(input, result, fmt);
  return (
    <>
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.pValue}
          value={<Probability fmt={fmt} value={result.p} />}
        />
        <StatCard label={strings.statistic} value={report.statistic} />
        {alpha === null ? null : (
          <StatCard
            label={t(strings.decisionLabel, { alpha: alphaText(alpha, fmt) })}
            value={
              result.p <= alpha ? strings.significant : strings.notSignificant
            }
          />
        )}
      </StatGrid>
      {alpha === null ? null : (
        <Decision alpha={alpha} fmt={fmt} p={result.p} strings={strings} />
      )}
      {input.statistic === "r" ? (
        <p className="font-mono text-muted-foreground text-sm">
          {t(strings.conversion, {
            df: formatDf(result.df as number, fmt),
            t: fmt(result.testValue, { maximumFractionDigits: 4 }),
          })}
        </p>
      ) : null}
      <div className={`${CARD} space-y-3`}>
        <h3 className={WIDGET_H3}>{strings.apaHeading}</h3>
        <p className="font-mono text-foreground">{report.plain}</p>
        <p className="text-muted-foreground text-xs">{strings.apaNote}</p>
        <CopyButton
          copiedLabel={strings.copied}
          html={report.html}
          label={strings.copyApa}
          small
          text={report.plain}
        />
      </div>
      <TailTable fmt={fmt} result={result} strings={strings} />
      <Sketch fmt={fmt} result={result} strings={strings} tail={input.tail} />
    </>
  );
}

export default function PValueCalculatorWidget() {
  const strings = useToolUi(P_VALUE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [form, setForm] = useState<FormState>(INITIAL);
  const update = (patch: Partial<FormState>) =>
    setForm((current) => ({ ...current, ...patch }));

  const input = readInput(form);
  const problem = input === null ? null : checkPValueInput(input);
  const result = input === null ? null : computePValue(input);
  const parsedAlpha = parseNumberInput(form.alphaInput);
  const alpha =
    parsedAlpha !== null && parsedAlpha > 0 && parsedAlpha < 1
      ? parsedAlpha
      : null;

  return (
    <>
      <PValueForm form={form} strings={strings} update={update} />
      <section aria-live="polite" className="space-y-4">
        {problem ? (
          <ErrorNotice>{strings[PROBLEM_KEYS[problem]]}</ErrorNotice>
        ) : null}
        {problem || result ? null : <Notice>{strings.enterPrompt}</Notice>}
        {alpha === null ? (
          <ErrorNotice>{strings.problemAlpha}</ErrorNotice>
        ) : null}
        {input && result ? (
          <PValueResults
            alpha={alpha}
            fmt={fmt}
            input={input}
            result={result}
            strings={strings}
          />
        ) : null}
      </section>
    </>
  );
}
